import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  CheckCircle2,
  Inbox,
  ListChecks,
  Plus,
  Route,
  Send,
  Settings2,
  TabletSmartphone,
  Timer,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/AuthProvider';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { NewTicketDialog } from '@/components/tickets/NewTicketDialog';
import { TicketCategoriesDialog } from '@/components/tickets/TicketCategoriesDialog';
import { TicketDetailDrawer } from '@/components/tickets/TicketDetailDrawer';
import {
  PersonChip,
  TicketCategoryLabel,
  TicketPriorityChip,
  TicketRef,
  TicketStatusChip,
} from '@/components/tickets/TicketChips';
import {
  useTicketCategories,
  useTickets,
  useTicketViews,
  useTicketsRealtime,
  type TicketView,
  type TicketWithContext,
} from '@/features/tickets/hooks';
import {
  TICKET_PRIORITIES,
  TICKET_PRIORITY_META,
  TICKET_STATUSES,
  TICKET_STATUS_META,
  isTicketClosed,
  personLabel,
  ticketRef,
} from '@/features/tickets/model';
import { formatDateOnly, formatRelative } from '@/lib/formatters';

const UNASSIGNED = '__unassigned__';
const UNCATEGORISED = '__none__';

const VIEWS: Array<{ id: TicketView; label: string; hint: string }> = [
  { id: 'inbox', label: 'Inbox', hint: 'Open requests waiting on you' },
  { id: 'mine', label: 'My requests', hint: 'Things you have asked for' },
  { id: 'all', label: 'All open', hint: 'Every open request you can see' },
  { id: 'closed', label: 'Closed', hint: 'Done, declined and cancelled' },
];

function isTicketView(value: string | null): value is TicketView {
  return value === 'inbox' || value === 'mine' || value === 'all' || value === 'closed';
}

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Tasks → Ticket inbox. One place to see every request that has been
 * sent to you (or, for the triage tier, every request nobody has routed
 * yet), the things you have asked for, and the history. Rows open the
 * detail drawer at /tickets/:id so links in notifications land here.
 */
export function TicketsPage() {
  const { role, profile } = useAuth();
  const canTriage = role === 'super_admin' || role === 'admin' || role === 'manager';
  const canManageCategories = role === 'super_admin' || role === 'admin';
  const navigate = useNavigate();
  const { id: openTicketId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  useTicketsRealtime();
  const ticketsQ = useTickets();
  const categoriesQ = useTicketCategories({ includeInactive: true });
  const { byView, counts } = useTicketViews(ticketsQ.data, canTriage);

  const viewParam = searchParams.get('view');
  const view: TicketView = isTicketView(viewParam) ? viewParam : 'inbox';
  const setView = useCallback(
    (next: TicketView) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'inbox') params.delete('view');
      else params.set('view', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const [creating, setCreating] = useState(false);
  const [managingCategories, setManagingCategories] = useState(false);

  const closeDrawer = useCallback(() => {
    const qs = searchParams.toString();
    navigate(qs ? `/tickets?${qs}` : '/tickets', { replace: true });
  }, [navigate, searchParams]);

  const rows = byView[view];
  const today = todayIso();

  const summary = useMemo(() => {
    const all = ticketsQ.data ?? [];
    const open = all.filter((t) => !isTicketClosed(t.status));
    const me = profile?.id;
    return {
      handling: open.filter((t) => t.assignee_id === me).length,
      overdue: open.filter((t) => t.needed_by && t.needed_by < today).length,
      closedMine: all.filter((t) => t.requester_id === me && isTicketClosed(t.status)).length,
      doneThisWeek: all.filter(
        (t) =>
          t.status === 'done' &&
          t.resolved_at &&
          Date.now() - new Date(t.resolved_at).getTime() < 7 * 86_400_000,
      ).length,
    };
  }, [ticketsQ.data, profile?.id, today]);

  const columns = useMemo<ColumnDef<TicketWithContext>[]>(
    () => [
      {
        id: 'ref',
        accessorFn: (row) => ticketRef(row.number),
        header: '#',
        size: 88,
        minSize: 72,
        cell: ({ row }) => <TicketRef number={row.original.number} />,
      },
      {
        id: 'title',
        accessorFn: (row) => `${row.title} ${row.description ?? ''}`,
        header: 'Request',
        size: 340,
        minSize: 200,
        sortingFn: (a, b) => a.original.title.localeCompare(b.original.title),
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate font-medium text-foreground">{row.original.title}</span>
              {row.original.source === 'work_mode' && (
                <TabletSmartphone
                  className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  aria-label="Submitted from Work Mode"
                />
              )}
            </div>
            {row.original.description && (
              <div className="truncate text-xs text-muted-foreground">{row.original.description}</div>
            )}
          </div>
        ),
      },
      {
        id: 'status',
        accessorFn: (row) => row.status,
        header: 'Status',
        size: 130,
        filterFn: arrIncludesSome,
        sortingFn: (a, b) =>
          TICKET_STATUS_META[a.original.status].order - TICKET_STATUS_META[b.original.status].order,
        cell: ({ row }) => <TicketStatusChip status={row.original.status} />,
      },
      {
        id: 'priority',
        accessorFn: (row) => row.priority,
        header: 'Priority',
        size: 120,
        filterFn: arrIncludesSome,
        sortingFn: (a, b) =>
          TICKET_PRIORITY_META[a.original.priority].order -
          TICKET_PRIORITY_META[b.original.priority].order,
        cell: ({ row }) => <TicketPriorityChip priority={row.original.priority} />,
      },
      {
        id: 'category',
        accessorFn: (row) => row.category?.id ?? UNCATEGORISED,
        header: 'Category',
        size: 160,
        filterFn: arrIncludesSome,
        sortingFn: (a, b) =>
          (a.original.category?.name ?? '').localeCompare(b.original.category?.name ?? ''),
        cell: ({ row }) => <TicketCategoryLabel category={row.original.category} />,
      },
      {
        id: 'requester',
        accessorFn: (row) => personLabel(row.requester, ''),
        header: 'From',
        size: 170,
        cell: ({ row }) => (
          <PersonChip person={row.original.requester} fallback="Former teammate" size="xs" />
        ),
      },
      {
        id: 'assignee',
        accessorFn: (row) => row.assignee_id ?? UNASSIGNED,
        header: 'Handled by',
        size: 170,
        filterFn: arrIncludesSome,
        sortingFn: (a, b) =>
          personLabel(a.original.assignee, '').localeCompare(personLabel(b.original.assignee, '')),
        cell: ({ row }) => (
          <PersonChip
            person={row.original.assignee}
            fallback={isTicketClosed(row.original.status) ? 'Nobody' : 'Unrouted'}
            size="xs"
            muted={!row.original.assignee}
          />
        ),
      },
      {
        id: 'needed_by',
        accessorFn: (row) => row.needed_by ?? '',
        header: 'Needed by',
        size: 130,
        cell: ({ row }) => {
          const value = row.original.needed_by;
          if (!value) return <span className="text-muted-foreground">—</span>;
          const late = !isTicketClosed(row.original.status) && value < today;
          return (
            <span className={cn('whitespace-nowrap', late && 'font-medium text-fresh-salmon-dark')}>
              {formatDateOnly(value)}
            </span>
          );
        },
      },
      {
        id: 'last_activity_at',
        accessorFn: (row) => row.last_activity_at,
        header: 'Last activity',
        size: 140,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatRelative(row.original.last_activity_at)}
          </span>
        ),
      },
      {
        id: 'created_at',
        accessorFn: (row) => row.created_at,
        header: 'Opened',
        size: 130,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDateOnly(row.original.created_at)}
          </span>
        ),
      },
    ],
    [today],
  );

  const filters: FacetedFilter[] = useMemo(() => {
    const assignees = new Map<string, string>();
    for (const t of ticketsQ.data ?? []) {
      if (t.assignee_id && t.assignee) assignees.set(t.assignee_id, personLabel(t.assignee));
    }
    const statuses = view === 'closed'
      ? TICKET_STATUSES.filter((s) => TICKET_STATUS_META[s].closed)
      : view === 'mine'
        ? TICKET_STATUSES
        : TICKET_STATUSES.filter((s) => !TICKET_STATUS_META[s].closed);
    return [
      {
        columnId: 'status',
        title: 'Status',
        options: statuses.map((value) => ({ value, label: TICKET_STATUS_META[value].label })),
      },
      {
        columnId: 'priority',
        title: 'Priority',
        options: TICKET_PRIORITIES.map((value) => ({
          value,
          label: TICKET_PRIORITY_META[value].label,
        })),
      },
      {
        columnId: 'category',
        title: 'Category',
        options: [
          ...(categoriesQ.data ?? []).map((c) => ({ value: c.id, label: c.name })),
          { value: UNCATEGORISED, label: 'Uncategorised' },
        ],
      },
      {
        columnId: 'assignee',
        title: 'Handled by',
        options: [
          { value: UNASSIGNED, label: 'Unrouted' },
          ...Array.from(assignees.entries())
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([value, label]) => ({ value, label })),
        ],
      },
    ];
  }, [ticketsQ.data, categoriesQ.data, view]);

  const emptyState = useMemo(() => {
    const newButton = (
      <Button onClick={() => setCreating(true)}>
        <Plus className="h-4 w-4" />
        New ticket
      </Button>
    );
    switch (view) {
      case 'inbox':
        return (
          <EmptyState
            icon={Inbox}
            title="Nothing waiting on you"
            detail={
              canTriage
                ? 'New requests land here until someone routes them, and anything sent to you stays here until it is done.'
                : 'Requests that other people send you will show up here.'
            }
            action={newButton}
          />
        );
      case 'mine':
        return (
          <EmptyState
            icon={Send}
            title="You have not asked for anything yet"
            detail="A tool, an order, access to something, a prototype, an idea — file it and follow it here instead of in Slack."
            action={newButton}
          />
        );
      case 'all':
        return <EmptyState icon={ListChecks} title="No open requests" detail="Everything is done, declined or cancelled." action={newButton} />;
      case 'closed':
        return <EmptyState icon={CheckCircle2} title="Nothing closed yet" detail="Finished and declined requests are kept here for the record." />;
    }
  }, [view, canTriage]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Tasks"
        title="Ticket inbox"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canManageCategories && (
              <Button variant="outline" onClick={() => setManagingCategories(true)}>
                <Settings2 className="h-4 w-4" />
                Categories
              </Button>
            )}
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New ticket
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {canTriage ? (
          <MetricCard
            label="Needs routing"
            value={String(counts.unrouted)}
            loading={ticketsQ.isLoading}
            icon={<Route className="h-4 w-4" />}
            tone={counts.unrouted > 0 ? 'negative' : 'neutral'}
            hint={counts.unrouted > 0 ? 'Open requests nobody has picked up' : 'Everything has an owner'}
          />
        ) : (
          <MetricCard
            label="Handling"
            value={String(summary.handling)}
            loading={ticketsQ.isLoading}
            icon={<Inbox className="h-4 w-4" />}
            tone="info"
            hint="Open requests sent to you"
          />
        )}
        <MetricCard
          label="Waiting on me"
          value={String(counts.waitingOnMe)}
          loading={ticketsQ.isLoading}
          icon={<Timer className="h-4 w-4" />}
          tone={counts.waitingOnMe > 0 ? 'negative' : 'neutral'}
          hint="Sent to you and not started yet"
        />
        <MetricCard
          label="Open"
          value={String(counts.all)}
          loading={ticketsQ.isLoading}
          icon={<ListChecks className="h-4 w-4" />}
          tone={summary.overdue > 0 ? 'negative' : 'info'}
          hint={
            summary.overdue > 0
              ? `${summary.overdue} past ${summary.overdue === 1 ? 'its' : 'their'} needed-by date`
              : summary.doneThisWeek > 0
                ? `${summary.doneThisWeek} finished in the last 7 days`
                : 'Nothing overdue'
          }
        />
        <MetricCard
          label="My requests"
          value={String(counts.mine)}
          loading={ticketsQ.isLoading}
          icon={<Send className="h-4 w-4" />}
          tone="neutral"
          hint={
            summary.closedMine > 0
              ? `Open · ${summary.closedMine} closed`
              : 'Things you have asked other people for'
          }
        />
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div
            role="tablist"
            aria-label="Ticket views"
            className="flex flex-wrap items-center gap-2"
          >
            {VIEWS.map((v) => {
              const active = view === v.id;
              const count = counts[v.id];
              return (
                <button
                  key={v.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  title={v.hint}
                  onClick={() => setView(v.id)}
                  className={cn(
                    'inline-flex min-h-11 items-center gap-2 rounded-full border px-3.5 text-sm transition-colors sm:min-h-9',
                    active
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border bg-card text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
                  )}
                >
                  {v.label}
                  <span
                    className={cn(
                      'inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs tabular-nums',
                      active ? 'bg-background/20 text-background' : 'bg-secondary text-muted-foreground',
                      v.id === 'inbox' && count > 0 && !active && 'bg-fresh-salmon/30 text-fresh-salmon-dark',
                    )}
                  >
                    {ticketsQ.isLoading ? '…' : count}
                  </span>
                </button>
              );
            })}
          </div>

          {ticketsQ.error ? (
            <EmptyState
              icon={Inbox}
              title="Could not load tickets"
              detail={
                ticketsQ.error instanceof Error
                  ? ticketsQ.error.message
                  : 'Apply the latest database migration and try again.'
              }
            />
          ) : (
            <DataTable
              key={view}
              data={rows}
              columns={columns}
              isLoading={ticketsQ.isLoading}
              getRowId={(row) => row.id}
              onRowClick={(row) => navigate(`/tickets/${row.id}${searchParams.toString() ? `?${searchParams}` : ''}`)}
              searchPlaceholder="Search tickets, people, categories…"
              facetedFilters={filters}
              tableId={`tickets-${view}`}
              initialColumnVisibility={{ created_at: false }}
              initialPageSize={50}
              rowClassName={(row) => (isTicketClosed(row.status) ? 'opacity-70' : undefined)}
              mobileCard={{
                title: (row) => row.title,
                subtitle: (row) =>
                  [ticketRef(row.number), row.category?.name].filter(Boolean).join(' · '),
                badges: (row) => [
                  <TicketStatusChip key="status" status={row.status} />,
                  ...(row.priority === 'high' || row.priority === 'urgent'
                    ? [<TicketPriorityChip key="priority" priority={row.priority} />]
                    : []),
                  ...(row.source === 'work_mode'
                    ? [
                        <Badge key="floor" variant="info-soft" className="gap-1 rounded-full">
                          <TabletSmartphone className="h-3 w-3" /> Floor
                        </Badge>,
                      ]
                    : []),
                ],
                meta: (row) => [
                  { label: 'From', value: personLabel(row.requester, 'Former teammate') },
                  {
                    label: 'Handled by',
                    value: row.assignee ? personLabel(row.assignee) : 'Unrouted',
                  },
                  ...(row.needed_by
                    ? [{ label: 'Needed by', value: formatDateOnly(row.needed_by) }]
                    : []),
                  { label: 'Activity', value: formatRelative(row.last_activity_at) },
                ],
                dimmed: (row) => isTicketClosed(row.status),
              }}
              emptyState={emptyState}
            />
          )}
        </CardContent>
      </Card>

      <TicketDetailDrawer
        ticketId={openTicketId ?? null}
        open={!!openTicketId}
        onOpenChange={(open) => {
          if (!open) closeDrawer();
        }}
      />

      <NewTicketDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(ticketId) => navigate(`/tickets/${ticketId}`)}
      />

      {canManageCategories && (
        <TicketCategoriesDialog open={managingCategories} onOpenChange={setManagingCategories} />
      )}
    </div>
  );
}
